import { useEffect, useState, useRef } from "react";
import { visionURL } from "../config/environment";
import visionCallService from "../services/visionCallService";
import getToken from "../services/tokenService";
import Jsona from "jsona";
import JsonApiService from "../services/jsonapiService";
import normalize from "../redux/utils/normalize";
import { uploadFile } from "../services/fileUploadService";

const dataFormatter = new Jsona();

const repositoryApiService = async (resource = "libraries", options = {}) => {
  const baseUrl = `${visionURL}/repository/api/v1`;
  options = { ...options, ...{ baseUrl } };
  const service = new JsonApiService(resource, options);
  const token = await getToken();
  return service.setAuthToken(token);
};

export const useRepositoryItems = () => {
  return {
    deleteItem: async itemId => {
      const service = await repositoryApiService("items");
      return await service.delete(itemId);
    },
    createItem: async ({ newItem, skipScormImport }) => {
      let fileId = null;
      const { file, ...newItemData } = newItem;
      if (file) {
        fileId = await uploadFile(file).then(response => response.fileId);
      }
      const service = await repositoryApiService("items");
      const response = await service
        .customParams({ skipScormImport })
        .create({ ...newItemData, fileId, type: "items" })
        .then(response => response.json());
      if (response.errors) {
        throw response.errors;
      } else {
        return dataFormatter.deserialize(response);
      }
    },
    createItemVersion: async (itemId, newItemVersionData) => {
      const service = await repositoryApiService("items");
      const response = await service
        .createRelated(
          itemId,
          "item_versions",
          visionCallService.serializeRequestBody(newItemVersionData, "items")
        )
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    },
    activateItemVersion: async (itemId, itemVersionId) => {
      const service = await repositoryApiService("items");
      const response = await service
        .updateRelatedResource(
          itemId,
          "item_versions",
          itemVersionId,
          visionCallService.serializeRequestBody({ activate: true }, "items")
        )
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    },
    fetchExtendable: async (itemId, options) => {
      const service = await repositoryApiService("extendables");
      const response = await service
        .include(options.include)
        .find(itemId)
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    },
    fetchItem: async (itemId, options) => {
      const service = await repositoryApiService("items");
      const response = await service
        .include(options.include)
        .find(itemId)
        .then(response => response.json());
      return normalize(response);
    }
  };
};

export const useRepositoryHomework = existingHomework => {
  const [homework, setHomework] = useState(null);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    setHomework(existingHomework);
  }, [existingHomework]);

  return {
    homework,
    loading,
    updateHomework: async newHomeworkData => {
      setLoading(true);
      const service = await repositoryApiService("homework");
      const response = await service
        .update(
          newHomeworkData.id,
          visionCallService.serializeRequestBody(
            {
              ...newHomeworkData,
              relationshipNames: [
                "item",
                "subscribedEmails",
                "fileFormats",
                "homeworkAssignmentAttachments"
              ]
            },
            "homework",
            [
              "item",
              "subscribedEmails",
              "fileFormats",
              "homeworkAssignmentAttachments"
            ]
          )
        )
        .then(response => response.json());
      return dataFormatter.deserialize(response);
    },
    bulkHomeworkAttachments: async (homework, attachments) => {
      const service = await repositoryApiService(
        "bulk_homework_assignment_attachments"
      );
      await service.create(
        visionCallService.serializeRequestBody(
          {
            homework,
            attachments,
            relationshipNames: ["attachments", "homework"]
          },
          "bulkHomeworkAttachments"
        )
      );
    }
  };
};

export const useRepositoryQuiz = existingQuiz => {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(null);
  const [quizErrors, setQuizErrors] = useState(null);

  const latestQuizValue = useRef(quiz);

  useEffect(() => {
    if (existingQuiz) {
      setQuiz(quiz => {
        latestQuizValue.current = existingQuiz;
        return existingQuiz;
      });
    }
  }, [existingQuiz]);

  return {
    quiz,
    loading,
    quizErrors,
    getQuiz: () => {
      return latestQuizValue.current;
    },
    fetchQuiz: async quizId => {
      const service = await repositoryApiService("quizzes");
      const response = await service
        .find(quizId)
        .then(response => response.json());
      const currentQuiz = dataFormatter.deserialize(response);
      setQuiz(currentQuiz);
      return currentQuiz;
    },
    createQuiz: async newQuizData => {
      setLoading(true);
      const service = await repositoryApiService("quizzes");
      const response = await service
        .create(visionCallService.serializeRequestBody(newQuizData, "quizzes"))
        .then(response => response.json());
      if (response.errors) {
        setQuizErrors(response.errors);
      } else {
        const updatedQuiz = dataFormatter.deserialize(response);
        setQuiz(quiz => {
          latestQuizValue.current = updatedQuiz;
          return updatedQuiz;
        });
      }
      setLoading(false);
      return response;
    },
    updateQuiz: async newQuizData => {
      setLoading(true);
      const service = await repositoryApiService("quizzes");
      const response = await service
        .update(
          newQuizData.id,
          visionCallService.serializeRequestBody(newQuizData, "quizzes")
        )
        .then(response => response.json());
      if (response.errors) {
        setQuizErrors(response.errors);
      } else {
        const updatedQuiz = dataFormatter.deserialize(response);
        setQuiz(updatedQuiz);
      }
      setLoading(false);
      return response;
    },
    createQuestion: async newQuestionData => {
      setLoading(true);
      const service = await repositoryApiService("questions");
      const response = await service
        .create(
          visionCallService.serializeRequestBody(newQuestionData, "questions", [
            "answers",
            "matchClues",
            "matchChoices",
            "fillInTheBlanks"
          ])
        )
        .then(response => response.json());
      const newQuestion = dataFormatter.deserialize(response);
      const updatedQuiz = { ...quiz };
      updatedQuiz.questions = [...updatedQuiz.questions, newQuestion];
      setQuiz(updatedQuiz);
      setLoading(false);
      return newQuestion;
    },
    updateQuestion: async (questionId, newQuestionData) => {
      setLoading(true);
      const service = await repositoryApiService("questions");
      const response = await service
        .update(
          questionId,
          visionCallService.serializeRequestBody(newQuestionData, "questions", [
            "answers",
            "matchClues",
            "matchChoices",
            "fillInTheBlanks"
          ])
        )
        .then(response => response.json());

      const updatedQuestion = dataFormatter.deserialize(response);
      const newQuestions = quiz.questions.map(question => {
        if (question.id === questionId) {
          return updatedQuestion;
        } else {
          return question;
        }
      });
      const updatedQuiz = { ...quiz };
      updatedQuiz.questions = newQuestions;
      setQuiz(updatedQuiz);
      setLoading(false);
      return updatedQuestion;
    },
    bulkUpdateQuestions: async newQuestionsData => {
      setLoading(true);
      const token = await getToken();
      const questionsUrl = `${visionURL}/repository/api/v1/questions/bulk_update`;
      return visionCallService.requestWithNormalizer(
        questionsUrl,
        response => {
          setLoading(false);
          return response;
        },
        error => {
          setLoading(false);
          return error;
        },
        "PATCH",
        newQuestionsData,
        ["answers"],
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );
    },
    updateQuizQuestionsRelationships: async quizData => {
      setLoading(true);
      const service = await repositoryApiService("quizzes");
      const response = await service
        .createRelationships(
          quiz.id,
          "questions",
          visionCallService.serializeRequestBody(quizData)
        )
        .then(response => response.json());
      setLoading(false);
      return dataFormatter.deserialize(response);
    },
    importQuizQuestions: async (file, enforceCleanup = false) => {
      setLoading(true);
      const importUrl = `${visionURL}/repository/api/v1/quizzes/${latestQuizValue.current.id}/quiz_imports`;
      const token = await getToken();
      const headers = {
        Authorization: `Bearer ${token}`
      };
      const data = {
        file,
        quizType: latestQuizValue.current.quizType,
        enforceCleanup: enforceCleanup
      };
      const formData = visionCallService.normalizeFormData(data);
      const response = await fetch(importUrl, {
        method: "POST",
        credentials: "include",
        body: formData,
        headers
      }).then(response => response.json());
      if (response.errors) {
        setQuizErrors(response.errors);
      } else {
        const updatedQuiz = dataFormatter.deserialize(response);
        setQuiz(updatedQuiz);
        if (response.meta && response.meta.errors) {
          setQuizErrors(response.meta.errors);
        }
      }
      setLoading(false);
      return response;
    }
  };
};
